import type { InstrumentDefinition } from "./instrument";
import { cableEndpoints, type Cable, type CableEndpoint } from "./patch";
import { validateSignalPatch, type SignalWarning } from "./signal-graph";

export type CableDraftStatus = "incomplete" | "valid" | "warning" | "invalid";

export type CableDraftIssue = {
  severity: "info" | "warning" | "error";
  message: string;
};

export type CableDraftAnalysis = {
  status: CableDraftStatus;
  issues: CableDraftIssue[];
};

const DRAFT_CABLE_ID = "__cable-draft__";

function samePort(a: CableEndpoint, b: CableEndpoint) {
  return a.kind === "port" && b.kind === "port" && a.portId === b.portId;
}

function stackable(endpoint: CableEndpoint) {
  return endpoint.kind === "port" && endpoint.plugStyle === "stackable";
}

function endpointAtPort(cable: Cable, portId: string) {
  return cableEndpoints(cable).find(
    (endpoint) => endpoint.kind === "port" && endpoint.portId === portId,
  );
}

function sameRoute(cable: Cable, a: CableEndpoint, b: CableEndpoint) {
  if (a.kind !== "port" || b.kind !== "port") return false;
  if (cable.endpointA.kind !== "port" || cable.endpointB.kind !== "port") return false;
  return (
    (cable.endpointA.portId === a.portId && cable.endpointB.portId === b.portId) ||
    (cable.endpointA.portId === b.portId && cable.endpointB.portId === a.portId)
  );
}

function warningToIssue(warning: SignalWarning): CableDraftIssue {
  return { severity: warning.severity, message: warning.message.replace("Cable 1", "This cable") };
}

export function analyzeCableDraft(
  definition: InstrumentDefinition,
  existingCables: Cable[],
  endpointA: CableEndpoint | null,
  endpointB: CableEndpoint | null,
  options: { allowFullyLoose?: boolean } = {},
): CableDraftAnalysis {
  if (!endpointA || !endpointB) {
    return {
      status: "incomplete",
      issues: [{ severity: "info", message: "Choose both cable endpoints to check the route." }],
    };
  }

  const issues: CableDraftIssue[] = [];
  if (endpointA.kind === "loose" && endpointB.kind === "loose" && !options.allowFullyLoose) {
    issues.push({
      severity: "error",
      message:
        "At least one end must be connected, unless this is documented as a prepared loose cable.",
    });
  }
  if (samePort(endpointA, endpointB)) {
    issues.push({
      severity: "error",
      message: "A cable cannot connect a patch point back to itself.",
    });
  }
  for (const endpoint of [endpointA, endpointB]) {
    if (endpoint.kind === "port" && !definition.ports.some((port) => port.id === endpoint.portId)) {
      issues.push({
        severity: "error",
        message: `Patch point “${endpoint.portId}” is not present on this instrument.`,
      });
    }
  }
  if (issues.some((issue) => issue.severity === "error")) {
    return { status: "invalid", issues };
  }

  const draftCable: Cable = {
    id: DRAFT_CABLE_ID,
    number: 1,
    colour: "#ffffff",
    endpointA,
    endpointB,
  };
  issues.push(
    ...validateSignalPatch(definition, [...existingCables, draftCable])
      .filter((warning) => warning.cableId === DRAFT_CABLE_ID)
      .map(warningToIssue),
  );

  if (existingCables.some((cable) => sameRoute(cable, endpointA, endpointB))) {
    issues.push({
      severity: "warning",
      message: "This route is already documented by another cable.",
    });
  }

  for (const endpoint of [endpointA, endpointB]) {
    if (endpoint.kind !== "port") continue;
    const existingEndpoints = existingCables
      .map((cable) => endpointAtPort(cable, endpoint.portId))
      .filter((candidate): candidate is Extract<CableEndpoint, { kind: "port" }> =>
        Boolean(candidate),
      );
    if (!existingEndpoints.length) continue;
    if (stackable(endpoint) || existingEndpoints.some(stackable)) continue;
    issues.push({
      severity: "warning",
      message: `Patch point “${endpoint.portId}” is already occupied. Mark the shared plug as stackable or use a mult.`,
    });
  }

  return {
    status: issues.some((issue) => issue.severity === "warning") ? "warning" : "valid",
    issues,
  };
}
